import { useState, useEffect, useContext } from "react";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import "./Settings.css";
import otherincomeIcon from "../../../../assets/Image/expensetypeicon.png";
import LoadingOverlay from "../../../common/LoadingBar.js";
import loader from "../../../../assets/Image/Loader2.gif";
import { useToastHandler } from "../../../common/ToastHandler";
import { ToastContainer } from "react-toastify";
import useSwal from "../../../common/ReusableComponent/useSwal";
import PaginatedSearchSelect from "../../../common/ReusableComponent/PaginatedSearchSelect/PaginatedSearchSelect";
import ChipList from "../../../common/ReusableComponent/ChipList";
import { CustomButton } from "../../../common/ReusableComponent/CustomButton";
import { Trash2 } from "lucide-react";
import { fetchAllPaginatedOptions, getBulkResultMessage } from "./settingsConfigUtils";

function OtherIncomeType({ embedded = false }) {
  let { user } = useContext(AuthContext);
  const storeId = user.store;
  const { confirmDelete } = useSwal()
  const [incomes, setIncomes] = useState([]);
  const {GetPaginatedData , Post, Delete, refreshCache } = useApi();
  const [loading, setLoading] = useState(true);
  const [selectedIncomeList, setSelectedIncomeList] = useState([]);
  const { handleToast } = useToastHandler();

  const fetchIncomes = async () => {
    setLoading(true);
    try {
      const results = await fetchAllPaginatedOptions(GetPaginatedData, "otherIncomeData");
      const formattedData = Array.isArray(results)
        ? results.map(item => ({
            id: item.id,
            title: item.all_income_detail?.title || item.custom_title || "",
            description: item.all_income_detail?.description || "",
            All_income: item.All_income,
            store: item.store
          }))
        : [];
      setIncomes(formattedData);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setIncomes([]);
      console.error("Error fetching income types:", error);
      handleToast({
        type: "ERROR",
        message: "Failed to fetch income types.",
      });
    }
  };

  useEffect(() => {
    fetchIncomes();
  }, []);

  const handleIncomeSelectMultiple = (income) => {
    if (!income) return;
    const exists = selectedIncomeList.some(
      (item) => item.id === income.id
    );
    if (!exists) {
      setSelectedIncomeList((prev) => [...prev, income]);
    }
  };

  const handleRemoveIncome = (item) => {
    setSelectedIncomeList((prev) =>
      prev.filter((income) => income.id !== item.id)
    );
  };

  const handleSave = async () => {
    if (selectedIncomeList.length === 0) {
      handleToast({
        type: "WARNING",
        message: "Please select income type",
      });
      return;
    }
    try {
      const payload = selectedIncomeList.map((item) => ({
        store: storeId,
        All_income: item.isCustom ? null : item.id,
        custom_title: item.isCustom ? item.title : null,
      }));
      const response = await Post("otherIncomeData", payload);
      
      if (response.data.total_skipped > 0) {
        handleToast({
          type: "WARNING",
          message: response.data.skipped[0].message,
        });
      } else if (response.data.total_created > 0) {
        handleToast({
          type: "SUCCESS",
          message: "Income types added successfully!",
        });
      }
      
      setSelectedIncomeList([]);
      refreshCache("otherIncomeData", "get");
      await fetchIncomes();
    } catch (error) {
      console.error(error);
      const errorData = error.response?.data;
      let errorMessage = "Error adding income types";
      
      if (errorData?.total_skipped > 0) {
        errorMessage = errorData.skipped[0].message;
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }
      
      handleToast({
        type: "ERROR",
        message: errorMessage,
      });
    }
  };

  const handleAddAllDefaults = async () => {
    setLoading(true);
    try {
      const options = await fetchAllPaginatedOptions(GetPaginatedData, "allIncomeData");
      const existingIds = new Set(incomes.map((item) => String(item.All_income)).filter(Boolean));
      const payload = options
        .filter((item) => !existingIds.has(String(item.id)))
        .map((item) => ({ store: storeId, All_income: item.id }));
      if (!payload.length) {
        handleToast({ type: "INFO", message: "All default income types are already added." });
        return;
      }
      const response = await Post("otherIncomeData", payload);
      handleToast({ type: "SUCCESS", message: getBulkResultMessage(response, "default income types") });
      setSelectedIncomeList([]);
      refreshCache("otherIncomeData", "get");
      await fetchIncomes();
    } catch (error) {
      handleToast({ type: "ERROR", message: error.response?.data?.message || "Unable to add all default income types." });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await confirmDelete();
    if (!result.isConfirmed) return;
    try {
      await Delete("otherIncomeData", id);
      refreshCache("otherIncomeData", "get");
      await fetchIncomes();
      handleToast({
        type: "SUCCESS",
        message: "Income type deleted successfully.",
      });
    } catch (error) {
      console.error(
        "Error deleting income:",
        error.response ? error.response.data : error.message
      );
      handleToast({
        type: "ERROR",
        message: "Failed to delete income type.",
      });
    }
  };

  if (loading) {
    return (
      <LoadingOverlay loading={loading} loader={loader} />
    );
  }

  return (
    <>
      <div >
        {!embedded && <ToastContainer />}
        <div className={embedded ? "settings-embedded-tool-panel" : "responsive-box"}>
          <div className="input-container">
            <div className="input-row">
              <div className="form-group product-field">
                <PaginatedSearchSelect
                  api="allIncomeData"
                  GetPaginatedData={GetPaginatedData}
                  getOptionLabel={(opt) => opt.title || ""}
                  onSelect={handleIncomeSelectMultiple}
                  placeholder="Select Income Type"
                  pageSize={10}
                  allowCustom
                  customOptionLabel={(value) => `Add custom income type "${value}"`}
                  filterFn={(opt) =>
                    !selectedIncomeList.some((item) => String(item.id) === String(opt.id)) &&
                    !incomes.some(
                      (item) =>
                        String(item.All_income) === String(opt.id) ||
                        String(item.all_income) === String(opt.id)
                    )
                  }
                />
              </div>

              <CustomButton
                text="Add Other Income"
                icon={otherincomeIcon}
                iconPosition="right"
                onClick={handleSave}
              />
              <CustomButton text="Add All Defaults" onClick={handleAddAllDefaults} />
            </div>

            <ChipList
              items={selectedIncomeList}
              onRemove={handleRemoveIncome}
              displayField="title"
              keyField="id"
            />
          </div>
        </div>
        <div
          className={embedded ? "settings-embedded-tool-panel settings-embedded-table-panel" : "responsive-box"}
          style={{ marginTop: embedded ? "14px" : "20px" }}
        >
          <div className="settings-chip-board">
            {Array.isArray(incomes) && incomes.length > 0 ? (
              incomes.map((income) => (
                <div className="settings-config-chip" key={income.id}>
                  <span>{income?.title || "N/A"}</span>
                  <button
                    type="button"
                    className="settings-chip-delete"
                    aria-label={`Delete ${income?.title || "income type"}`}
                    onClick={() => handleDelete(income.id)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            ) : (
              <div className="settings-empty-state settings-table-empty">
                <strong>No income types added yet</strong>
                <span>Select income types above to keep ledger income clean.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default OtherIncomeType;
